import { useMemo } from 'react';
import {
  SortColumnDto,
  TableCacheKeyDto,
  TableColumnConfigDto,
} from '@asset-metrix/portal-gateway-client-js';
import { applyFormatters, FormatterOptions } from '@asset-metrix/portal-ui';
import { useQuery } from '@tanstack/react-query';

import {
  fetchDatatableData,
  FetchDatatableDataQueryKey,
} from '../../api/portalGateway/datatableData';
import i18n from '../../i18n/config';
import { useCombinedPageFilterSelections } from '../../store/hooks/useCombinedPageFilterSelections';
import { prependRoleId } from '../../utils/prependRoleId';
import { formattersByType } from '../table/configToColDef';

import {
  canResolveAllPlaceholders,
  extractPlaceholderKeys,
  hasPlaceholders,
  replacePlaceholders,
} from './parsePlaceholders';

type UseTextFromTableDataParams = {
  text: string;
  tableKey?: string;
  columns?: TableColumnConfigDto[];
  sortColumns?: SortColumnDto[];
  pageId: string;
  widgetId: string;
  pageFilterInstanceId: string;
};

type TextFromTableErrorType =
  | 'FETCH_ERROR'
  | 'MISSING_COLUMN_CONFIG'
  | 'NO_DATA_FOUND';

export class TextFromTableError extends Error {
  constructor(
    public readonly type: TextFromTableErrorType,
    message: string,
    public readonly details?: string[]
  ) {
    super(message);
    this.name = 'TextFromTableError';
  }
}

type UseTextFromTableDataResult = {
  formattedText: string;
  isLoading: boolean;
  error: TextFromTableError | null;
};

const DATATABLE_CACHE_KEY: TableCacheKeyDto = {
  dataSourceType: 'datatable',
};

/**
 * Hook to fetch and format data for text from table placeholders.
 * Returns the formatted text with placeholders replaced, or the original text
 * if there are no placeholders or if fetching is not needed.
 */
export function useTextFromTableData({
  text,
  tableKey,
  columns,
  sortColumns,
  pageId,
  widgetId,
  pageFilterInstanceId,
}: UseTextFromTableDataParams): UseTextFromTableDataResult {
  const combinedPageFilterSelections =
    useCombinedPageFilterSelections(pageFilterInstanceId);

  const placeholderKeys = useMemo(() => extractPlaceholderKeys(text), [text]);
  const hasPlaceholdersInText = useMemo(() => hasPlaceholders(text), [text]);

  const shouldFetchData = useMemo(
    () => !!tableKey && hasPlaceholdersInText,
    [tableKey, hasPlaceholdersInText]
  );

  const {
    data: tableData,
    isLoading,
    error,
  } = useQuery({
    queryKey: prependRoleId<FetchDatatableDataQueryKey>([
      'datatables',
      DATATABLE_CACHE_KEY,
      { pageId },
      { widgetId },
      {
        page: 0,
        pageSize: 1,
        pageFilter: combinedPageFilterSelections,
        tableFilter: {},
        sortItems: sortColumns ?? [],
      },
    ]),
    queryFn: fetchDatatableData,
    enabled: shouldFetchData,
  });

  const formattedValues = useMemo(() => {
    if (!tableData?.data?.items?.[0]) {
      return {};
    }

    const firstRow = tableData.data.items[0];
    const result: Record<string, string | undefined> = {};

    for (const key of placeholderKeys) {
      const rawValue = firstRow[key];
      const columnConfig = columns?.find((col) => col.key === key);

      if (columnConfig) {
        const formattedValue = formatValue(rawValue, columnConfig);
        result[key] = formattedValue;
      } else {
        // No column config found - this will be caught by resolvedError check
        // For now, set to undefined so it triggers the error
        result[key] = undefined;
      }
    }

    return result;
  }, [tableData, placeholderKeys, columns]);

  const resolvedError = useMemo(() => {
    // If there's a query error, return it as FETCH_ERROR
    if (error) {
      return new TextFromTableError('FETCH_ERROR', 'Failed to fetch data');
    }

    // Check if any placeholder keys are missing column configs (misconfiguration)
    // This handles both: columns is undefined (backend didn't provide configs)
    // and columns is an array but missing some placeholder keys
    if (shouldFetchData) {
      const missingConfigs = placeholderKeys.filter(
        (key) => !columns?.some((col) => col.key === key)
      );
      if (missingConfigs.length > 0) {
        return new TextFromTableError(
          'MISSING_COLUMN_CONFIG',
          'Missing column configuration',
          missingConfigs
        );
      }
    }

    // If data is loaded but placeholders can't be resolved, return an error
    if (
      shouldFetchData &&
      !isLoading &&
      tableData &&
      !canResolveAllPlaceholders(text, formattedValues)
    ) {
      return new TextFromTableError('NO_DATA_FOUND', 'No data found');
    }

    return null;
  }, [
    error,
    shouldFetchData,
    isLoading,
    tableData,
    text,
    formattedValues,
    placeholderKeys,
    columns,
  ]);

  const formattedText = useMemo(() => {
    // If no placeholders, return text as-is
    if (!hasPlaceholdersInText) {
      return text;
    }

    // If still loading or error occurred, return original text
    // (component will handle showing loading/error states)
    if (isLoading || resolvedError) {
      return text;
    }

    // Replace placeholders with formatted values
    return replacePlaceholders(text, formattedValues);
  }, [text, hasPlaceholdersInText, isLoading, resolvedError, formattedValues]);

  return {
    formattedText,
    isLoading: shouldFetchData && isLoading,
    error: resolvedError,
  };
}

/**
 * Format a value using the column configuration's formatters
 */
function formatValue(
  value: unknown,
  columnConfig: TableColumnConfigDto
): string | undefined {
  // undefined means value doesn't exist - return undefined to keep placeholder
  if (value === undefined) {
    return undefined;
  }

  const formatters = formattersByType[columnConfig.type] ?? [];

  if (formatters.length === 0) {
    return String(value);
  }

  const baseOptions: FormatterOptions = {
    dateFormat: columnConfig.dateFormat,
    digits: columnConfig.digits,
    factor: columnConfig.factor,
    postfix: columnConfig.postfix,
    disableThousandsSeparator: columnConfig.disableThousandsSeparator,
    displayFormat: columnConfig.displayFormat,
  };

  const options: FormatterOptions =
    columnConfig.type === 'text'
      ? {
          trueLabel: i18n.t('app.label.true'),
          falseLabel: i18n.t('app.label.false'),
          ...baseOptions,
        }
      : baseOptions;

  // Let formatters handle null (emptyValueFormatter will convert it to '--')
  const formatted = applyFormatters(formatters, value, options);
  return formatted != null ? String(formatted) : undefined;
}
